/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import './index.css';

import { $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import {
   $getSelection,
   $isRangeSelection,
   COMMAND_PRIORITY_CRITICAL,
   COMMAND_PRIORITY_HIGH,
   COMMAND_PRIORITY_LOW,
   KEY_ESCAPE_COMMAND,
   LexicalEditor,
   NodeSelection,
   RangeSelection,
   SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { TableSelection } from "@lexical/table"
import { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import LinkPreview from '../../ui/LinkPreview';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { setFloatingElemPosition } from '../../utils/setFloatingElemPosition';
import { sanitizeUrl } from '../../utils/url';

function FloatingLinkEditor({
   editor,
   isLink,
   setIsLink,
   anchorElem,
}: {
   editor: LexicalEditor;
   isLink: boolean;
   setIsLink: Dispatch<boolean>;
   anchorElem: HTMLElement;
}): JSX.Element {
   const editorRef = useRef<HTMLDivElement | null>(null);
   const inputRef = useRef<HTMLInputElement>(null);
   const [linkUrl, setLinkUrl] = useState('');
   const [isEditMode, setEditMode] = useState(false);
   const [lastSelection, setLastSelection] = useState<
      RangeSelection | TableSelection | any | NodeSelection | null
   >(null);

   const updateLinkEditor = useCallback(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
         const node: any = getSelectedNode(selection);
         const parent: any = node.getParent();
         if ($isLinkNode(parent)) {
            setLinkUrl(parent.getURL());
         } else if ($isLinkNode(node)) {
            setLinkUrl(node.getURL());
         } else {
            setLinkUrl('');
         }
      }
      const editorElem = editorRef.current;
      const nativeSelection = window.getSelection();
      const activeElement = document.activeElement;

      if (editorElem === null) {
         return;
      }

      const rootElement = editor.getRootElement();

      if (
         selection !== null &&
         nativeSelection !== null &&
         rootElement !== null &&
         rootElement.contains(nativeSelection.anchorNode) &&
         editor.isEditable()
      ) {
         const domRange = nativeSelection.getRangeAt(0);
         let rect;
         if (nativeSelection.anchorNode === rootElement) {
            let inner = rootElement;
            while (inner.firstElementChild != null) {
               inner = inner.firstElementChild as HTMLElement;
            }
            rect = inner.getBoundingClientRect();
         } else {
            rect = domRange.getBoundingClientRect();
         }

         setFloatingElemPosition(rect, editorElem, anchorElem);
         setLastSelection(selection);
      } else if (!activeElement || activeElement.className !== 'link-input') {
         if (rootElement !== null) {
            setFloatingElemPosition(null, editorElem, anchorElem);
         }
         setLastSelection(null);
         setEditMode(false);
         setLinkUrl('');
      }

      return true;
   }, [anchorElem, editor]);

   useEffect(() => {
      const scrollerElem = anchorElem.parentElement;

      const update = () => {
         editor.getEditorState().read(() => {
            updateLinkEditor();
         });
      };

      window.addEventListener('resize', update);

      if (scrollerElem) {
         scrollerElem.addEventListener('scroll', update);
      }

      return () => {
         window.removeEventListener('resize', update);

         if (scrollerElem) {
            scrollerElem.removeEventListener('scroll', update);
         }
      };
   }, [anchorElem.parentElement, editor, updateLinkEditor]);

   useEffect(() => {
      return mergeRegister(
         editor.registerUpdateListener(() => {
            editor.getEditorState().read(() => {
               updateLinkEditor();
            });
         }),

         editor.registerCommand(
            SELECTION_CHANGE_COMMAND,
            () => {
               editor.getEditorState().read(() => {
                  updateLinkEditor();
               });
               return true;
            },
            COMMAND_PRIORITY_LOW,
         ),
         editor.registerCommand(
            KEY_ESCAPE_COMMAND,
            () => {
               if (isLink) {
                  setIsLink(false);
                  return true;
               }
               return false;
            },
            COMMAND_PRIORITY_HIGH,
         ),
      );
   }, [editor, updateLinkEditor, setIsLink, isLink]);

   useEffect(() => {
      editor.getEditorState().read(() => {
         updateLinkEditor();
      });
   }, [editor, updateLinkEditor]);

   useEffect(() => {
      if (isEditMode && inputRef.current) {
         inputRef.current.focus();
      }
   }, [isEditMode]);

   const linkClickHandler = useCallback(() => {
      setEditMode(true)
   }, [])

   const preventDefault = (event: React.MouseEvent<any>) => event.preventDefault()
   const setLink = (event: React.ChangeEvent<HTMLInputElement>) => setLinkUrl(event.target.value)
   const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' || event.key === 'Escape') {
         event.preventDefault();
         if (lastSelection !== null) {
            if (linkUrl !== '') {
               editor.dispatchCommand(
                  TOGGLE_LINK_COMMAND,
                  sanitizeUrl(linkUrl),
               );
            }
            setEditMode(false);
         }
      }
   }

   return (
      <div ref={editorRef} className="link-editor">
         {isEditMode ? (
            <input
               ref={inputRef}
               className="link-input"
               value={linkUrl}
               onChange={setLink}
               onKeyDown={onKeyDown}
            />
         ) : (
            <>
               <div className="link-input">
                  <a href={linkUrl} target="_blank" rel="noopener noreferrer">
                     {linkUrl}
                  </a>
                  <div
                     className="link-edit"
                     role="button"
                     tabIndex={0}
                     onMouseDown={preventDefault}
                     onClick={linkClickHandler}
                  />
               </div>
               <LinkPreview url={linkUrl} />
            </>
         )}
      </div>
   );
}

function useFloatingLinkEditorToolbar(
   editor: LexicalEditor,
   anchorElem: HTMLElement,
): JSX.Element | null {
   const [activeEditor, setActiveEditor] = useState(editor);
   const [isLink, setIsLink] = useState(false);

   const updateToolbar = useCallback(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
         const node = getSelectedNode(selection);
         const linkParent = $findMatchingParent(node, (parent) => $isLinkNode(parent));
         const autoLinkParent = $findMatchingParent(node, (parent) => $isAutoLinkNode(parent));

         // We don't want this menu to open for auto links.
         if (linkParent != null && autoLinkParent == null) {
            setIsLink(true);
         } else {
            setIsLink(false);
         }
      }
   }, []);

   useEffect(() => {
      return editor.registerCommand(
         SELECTION_CHANGE_COMMAND,
         (_payload, newEditor) => {
            newEditor.getEditorState().read(() => {
               updateToolbar();
            });
            setActiveEditor(newEditor);
            return false;
         },
         COMMAND_PRIORITY_CRITICAL,
      );
   }, [editor, updateToolbar]);

   return isLink
      ? createPortal(
         <FloatingLinkEditor
            editor={activeEditor}
            isLink={isLink}
            anchorElem={anchorElem}
            setIsLink={setIsLink}
         />,
         anchorElem,
      )
      : null;
}

export default function FloatingLinkEditorPlugin({
   anchorElem = document.body,
}: {
   anchorElem?: HTMLElement;
}): JSX.Element | null {
   const [editor] = useLexicalComposerContext();
   return useFloatingLinkEditorToolbar(editor, anchorElem);
}
